"use client";

import MediaImage from "@/components/ui/MediaImage";

import { useEffect, useRef, useState } from "react";
import type Player from "@vimeo/player";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPause,
  faPlay,
  faVolume,
  faVolumeXmark,
} from "@fortawesome/sharp-light-svg-icons";
import { responsiveVariables } from "@/utils/visual-style";

export interface CoverVideoSettings {
  type: string;
  url: string;
  poster?: string;
  posterPriority?: "high" | "auto" | "low";
  title?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  /** false hides the mute and pause buttons; undefined keeps them for the legacy hero videos */
  controls?: boolean;
  showPlayPauseControl?: boolean;
  coverHeight?: string;
  position?: string;
  aspectRatio?: string;
}

export default function CoverVideo({
  config,
  embedUrl,
}: {
  config: CoverVideoSettings;
  embedUrl: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const vimeoHost = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const player = useRef<Player>();
  const youtube = useRef<(command: string) => void>();
  const requestedPlay = useRef(config.autoPlay ?? true);
  const inViewport = useRef(true);
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(config.muted ?? true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      setEnabled(true);
  }, []);
  useEffect(() => {
    if (config.type !== "vimeo" || !enabled || failed || !vimeoHost.current)
      return;
    const host = vimeoHost.current;
    let disposed = false;
    let instance: Player | undefined;
    import("@vimeo/player")
      .then(({ default: VimeoPlayer }) => {
        if (disposed) return;
        // Vimeo destroys its iframe on cleanup; keep that node outside React's ownership.
        const frame = document.createElement("iframe");
        frame.src = embedUrl;
        frame.title = config.title || "Video";
        frame.allow = "autoplay; fullscreen; picture-in-picture";
        host.replaceChildren(frame);
        instance = new VimeoPlayer(frame);
        player.current = instance;
        instance.on("play", () => {
          setReady(true);
          if (!inViewport.current) instance?.pause().catch(() => {});
          else setPlaying(true);
        });
        instance.on("pause", () => setPlaying(false));
        instance.on("ended", () => {
          requestedPlay.current = false;
          setPlaying(false);
        });
        instance.on("error", () => {
          setFailed(true);
          setReady(false);
        });
        instance
          .ready()
          .then(() => {
            if (disposed) return;
            setReady(true);
            if (requestedPlay.current && inViewport.current)
              instance?.play().catch(() => setPlaying(false));
            else instance?.pause().catch(() => {});
          })
          .catch(() => setFailed(true));
      })
      .catch(() => setFailed(true));
    return () => {
      disposed = true;
      player.current = undefined;
      instance?.destroy().catch(() => {});
    };
  }, [config.type, config.autoPlay, config.title, enabled, embedUrl, failed]);
  // YouTube has no player package here; its embed takes commands and reports its state through postMessage.
  useEffect(() => {
    if (config.type !== "youtube" || !enabled || failed || !vimeoHost.current)
      return;
    const host = vimeoHost.current;
    const source = new URL(embedUrl);
    const id =
      source.searchParams.get("v") ||
      source.pathname.split("/").filter(Boolean).pop() ||
      "";
    const embed = new URL(
      `https://${source.hostname.endsWith("youtube-nocookie.com") ? "www.youtube-nocookie.com" : "www.youtube.com"}/embed/${encodeURIComponent(id)}`,
    );
    if (source.searchParams.has("start"))
      embed.searchParams.set("start", source.searchParams.get("start")!);
    for (const [key, value] of Object.entries({
      autoplay: requestedPlay.current ? "1" : "0",
      mute: (config.muted ?? true) ? "1" : "0",
      loop: (config.loop ?? true) ? "1" : "0",
      playlist: id,
      controls: "0",
      playsinline: "1",
      rel: "0",
      enablejsapi: "1",
      origin: window.location.origin,
    }))
      embed.searchParams.set(key, value);
    const frame = document.createElement("iframe");
    frame.src = embed.toString();
    frame.title = config.title || "Video";
    frame.allow = "autoplay; encrypted-media; picture-in-picture";
    // The CMS preview document sends no referrer, and YouTube refuses an embed without one (error 153).
    frame.referrerPolicy = "strict-origin-when-cross-origin";
    const send = (func: string) =>
      frame.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*",
      );
    youtube.current = send;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.contentWindow) return;
      let data: { event?: string; info?: unknown };
      try {
        data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      const state =
        data?.event === "onStateChange"
          ? data.info
          : data?.event === "infoDelivery"
            ? (data.info as { playerState?: number } | undefined)?.playerState
            : undefined;
      if (data?.event === "onError") {
        setFailed(true);
        setReady(false);
      } else if (state === 1) {
        setReady(true);
        if (!inViewport.current) send("pauseVideo");
        else setPlaying(true);
      } else if (state === 2) setPlaying(false);
      else if (state === 0) {
        requestedPlay.current = false;
        setPlaying(false);
      }
    };
    window.addEventListener("message", onMessage);
    frame.addEventListener("load", () => {
      setReady(true);
      frame.contentWindow?.postMessage(
        JSON.stringify({ event: "listening", id: 1, channel: "widget" }),
        "*",
      );
    });
    host.replaceChildren(frame);
    return () => {
      window.removeEventListener("message", onMessage);
      youtube.current = undefined;
      host.replaceChildren();
    };
  }, [
    config.type,
    config.title,
    config.muted,
    config.loop,
    enabled,
    embedUrl,
    failed,
  ]);
  useEffect(() => {
    if (!container.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      inViewport.current = entry.isIntersecting;
      if (!entry.isIntersecting) {
        video.current?.pause();
        player.current?.pause().catch(() => {});
        youtube.current?.("pauseVideo");
      } else if (requestedPlay.current) {
        video.current?.play().catch(() => {});
        player.current?.play().catch(() => {});
        youtube.current?.("playVideo");
      }
    });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [enabled]);
  const toggleMute = async () => {
    const next = !muted;
    if (youtube.current) {
      youtube.current(next ? "mute" : "unMute");
      setMuted(next);
    } else if (player.current) {
      try {
        await player.current.setMuted(next);
        setMuted(next);
      } catch {
        setFailed(true);
      }
    } else if (video.current) {
      video.current.muted = next;
      setMuted(next);
    }
  };
  const togglePlay = async () => {
    if (!enabled || failed) {
      requestedPlay.current = true;
      setReady(false);
      setFailed(false);
      setEnabled(true);
      return;
    }
    requestedPlay.current = !playing;
    try {
      if (youtube.current) {
        youtube.current(playing ? "pauseVideo" : "playVideo");
        setPlaying(!playing);
      } else if (player.current)
        await (playing ? player.current.pause() : player.current.play());
      else if (video.current) {
        if (playing) video.current.pause();
        else await video.current.play();
      }
    } catch {
      setPlaying(false);
    }
  };
  return (
    <div
      ref={container}
      className="re-cover-video"
      style={
        {
          ...responsiveVariables(
            "cover-height",
            config.coverHeight,
            "430px | auto",
          ),
          aspectRatio: config.aspectRatio || "16 / 9",
        } as React.CSSProperties
      }
    >
      {config.poster && (
        <MediaImage
          src={config.poster}
          alt=""
          className="re-cover-video-poster"
          style={{ objectPosition: config.position || "center" }}
          fetchPriority={config.posterPriority ?? "high"}
        />
      )}
      {enabled &&
        !failed &&
        (config.type === "vimeo" || config.type === "youtube" ? (
          <div
            ref={vimeoHost}
            className={`re-cover-video-vimeo ${ready ? "is-ready" : ""}`}
          />
        ) : (
          <video
            ref={video}
            src={config.url}
            autoPlay={config.autoPlay ?? true}
            muted={config.muted ?? true}
            loop={config.loop ?? true}
            playsInline
            preload="metadata"
            onLoadedData={() => setReady(true)}
            onPlay={() => {
              if (!inViewport.current) video.current?.pause();
              else setPlaying(true);
            }}
            onPause={() => setPlaying(false)}
            onEnded={() => {
              requestedPlay.current = false;
              setPlaying(false);
            }}
            onError={() => {
              setFailed(true);
              setReady(false);
            }}
            className={ready ? "is-ready" : ""}
            style={{ objectPosition: config.position || "center" }}
          />
        ))}
      {config.controls !== false && (
        <div className="re-cover-video-controls">
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Slå lyd til" : "Slå lyd fra"}
            aria-pressed={!muted}
            disabled={!ready}
          >
            <FontAwesomeIcon icon={muted ? faVolumeXmark : faVolume} />
          </button>
          {config.showPlayPauseControl !== false && (
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? "Sæt video på pause" : "Afspil video"}
              aria-pressed={playing}
            >
              <FontAwesomeIcon icon={playing ? faPause : faPlay} />
            </button>
          )}
          {failed && <span role="status">Videoen kunne ikke indlæses</span>}
        </div>
      )}
    </div>
  );
}
